/**
 * Rango que el Anexo 20 tolera para el Importe de una retención.
 *
 * El SAT no exige que el Importe sea exactamente base × tasa: admite el
 * intervalo que resulta de la incertidumbre de redondeo de la base. Con MXN
 * (2 decimales) ese intervalo suele abarcar un centavo, y es ahí donde cabe el
 * ajuste que piden las dependencias de gobierno —SEDENA calcula la retención de
 * IVA como 2/3 del impuesto trasladado ya redondeado, no como base × tasa—.
 *
 * Espejo de `rango_importe_impuesto_sat` en backend/apps/financiero/decimal_utils.py.
 * El backend es la autoridad; esto solo da el aviso inmediato en pantalla.
 */

const DECIMALES_MONEDA = 2

/** Redondea a `decimals` evitando el error binario de los flotantes. */
const redondea = (valor, decimals = DECIMALES_MONEDA) => {
  const f = 10 ** decimals
  return Math.round((valor + Number.EPSILON) * f) / f
}

/**
 * Convierte una tasa capturada a fracción.
 * El formulario captura porcentaje (10.6667); el CFDI lleva fracción (0.106667).
 */
export const tasaAFraccion = (tasa) => {
  const t = parseFloat(tasa) || 0
  return t > 1 ? t / 100 : t
}

/**
 * Rango permitido para el importe de una retención.
 * @returns {{ inferior: number, superior: number }}
 */
export const rangoImporteRetencion = (base, tasa, decimals = DECIMALES_MONEDA) => {
  const b = parseFloat(base) || 0
  const t = tasaAFraccion(tasa)
  const delta = 10 ** -decimals / 2
  const f = 10 ** decimals
  const inferior = Math.floor((b - delta) * t * f) / f
  const superior = Math.ceil((b + delta) * t * f) / f
  return { inferior: Math.max(0, redondea(inferior, decimals)), superior: redondea(superior, decimals) }
}

/** True si el importe cae fuera de lo que el SAT admite para esa base y tasa. */
export const importeFueraDeRango = (base, tasa, importe, decimals = DECIMALES_MONEDA) => {
  const { inferior, superior } = rangoImporteRetencion(base, tasa, decimals)
  const imp = redondea(parseFloat(importe) || 0, decimals)
  return imp < inferior || imp > superior
}

/** Mensaje para el contador cuando el importe se sale del rango. */
export const mensajeFueraDeRango = (base, tasa, decimals = DECIMALES_MONEDA) => {
  const { inferior, superior } = rangoImporteRetencion(base, tasa, decimals)
  return `El SAT solo admite entre ${inferior.toFixed(decimals)} y ${superior.toFixed(decimals)} para esta base y tasa.`
}
